import { createStore } from '@/common/pinia';
import type { ApiResponse } from '@/common/services/api.service-new';
import { HANSEN_YEARLY_END, HANSEN_YEARLY_START } from '../hansen/hansen.const';
import type {
  FireLossCertaintyClass,
  FireLossResponse,
  FirelossCertaintyLayersResponse,
} from './fireloss.types';

/** Fire tiers selected by default — non-fire is map context, off by default. */
const DEFAULT_SELECTED_CERTAINTIES: FireLossCertaintyClass[] = ['high', 'medium', 'low'];

/**
 * State for the fire-loss feature (v2). Mirrors the relevant slice of the
 * Hansen store, plus the **shared certainty selection** that drives both the
 * map layers and the stats panel:
 *
 *  - `certaintyLayers` — session-global cache of the nested
 *    /fireloss/certainty-layers tile-URL map.
 *  - `selectedYearRange` — year-range driven by the tool's slider (full record
 *    2001-2025 by default).
 *  - `selectedCertainties` — which certainty classes are shown on the map AND
 *    counted in the stats. An array (replaced on toggle) so getters/computed
 *    that read it recompute reliably.
 *  - `lossStatsCache` — per-polygon /fireloss/loss cache by fingerprint.
 */
class FirelossStore {
  public certaintyLayers: ApiResponse<FirelossCertaintyLayersResponse> | null = null;
  public selectedYearRange: [number, number] = [HANSEN_YEARLY_START, HANSEN_YEARLY_END];
  public selectedCertainties: FireLossCertaintyClass[] = [...DEFAULT_SELECTED_CERTAINTIES];

  private lossStatsCache: Map<string, FireLossResponse> = new Map();

  public setCertaintyLayers(layers: ApiResponse<FirelossCertaintyLayersResponse> | null = null): void {
    this.certaintyLayers = layers;
  }

  public setSelectedYearRange(range: [number, number]): void {
    this.selectedYearRange = range;
  }

  public isCertaintySelected(certainty: FireLossCertaintyClass): boolean {
    return this.selectedCertainties.includes(certainty);
  }

  /** Toggle one certainty class. Replaces the array reference so reads react. */
  public toggleCertainty(certainty: FireLossCertaintyClass): void {
    this.selectedCertainties = this.selectedCertainties.includes(certainty)
      ? this.selectedCertainties.filter((c) => c !== certainty)
      : [...this.selectedCertainties, certainty];
  }

  public getLossStatsCache(fingerprint: string): FireLossResponse | undefined {
    return this.lossStatsCache.get(fingerprint);
  }

  public setLossStatsCache(fingerprint: string, stats: FireLossResponse): void {
    this.lossStatsCache.set(fingerprint, stats);
  }
}

const useFirelossStore = createStore(FirelossStore);

export { useFirelossStore, type FirelossStore };
